import { Typography } from "@mui/material";
import { get } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Row } from "reactstrap";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import { ITableColumn } from "src/components/FinalTable/FinalTable";
import { TableActions } from "src/components/TableV2/TableActions";
import Toast from "src/components/Toast/Toast";
import { ICommission } from "src/store/models/document.model";
import { IRootState } from "src/store/reducers";
import {
  addCommission,
  deleteCommission,
  fetchBrokerCommissionsList,
  fetchNewCommissionDTO,
  updateAllCommissions,
} from "src/store/reducers/brokerCommissions";
import { sanitizePercent } from "src/util/util.sanitizePercetange";
import { Container } from "../../../components/Container/Container";
import DisTable from "../../../components/TableV2/Table";
import * as S from "../../../constants/StringConstants";
import { formatDate } from "../../CampaignDetail/utils";
import "./BrokerCommissions.scss";
import CarrierModal from "./CarrierModal";
import CommissionHistoryModal from "./CommissionHistoryModal";
import CommissionTableModal from "./CommissionTable";

const BrokerCommissions = () => {
  const fileTableRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedCommissionId, setSelectedCommissionId] = useState<number | null>(null);
  const [commissions, setCommissions] = useState<ICommission[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [carrierOpen, setCarrierOpen] = useState(false);
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [deletedId, setDeletedId] = useState(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState(null);
  const dispatch = useDispatch();
  const { id: brokerID } = useParams<{ id: string }>();

  const { selectCarrierTypeList, loading } = useSelector((state: IRootState) => state.brokerCommissions);

  const startEditing = () => {
    const map: Record<number, string> = {};
    for (const c of commissions) map[c.commissionID] = String(c.overridePercentage ?? "");
    setEdits(map);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEdits({});
  };

  const saveEditing = async () => {
    // Build payload of ONLY changed records
    const changedRecords = commissions
      .filter(c => {
        const raw = edits[c.commissionID];
        if (raw === undefined) return false; // not touched
        const newVal = raw === "" ? c.overridePercentage : Number(raw);
        return newVal !== c.overridePercentage; // only changed ones
      })
      .map(c => {
        const raw = edits[c.commissionID];
        const overridePercentage = raw === "" ? c.overridePercentage : Number(raw);

        return {
          ...c,
          overridePercentage,
        };
      });

    if (changedRecords.length === 0) {
      setIsEditing(false);
      setEdits({});
      return;
    }

    try {
      await dispatch(updateAllCommissions(brokerID, changedRecords));
      setIsEditing(false);
      setEdits({});
      fileTableRef.current?.update?.();
    } catch (err) {}
  };

  const handleOverrideChange = (id: number, raw: string) => {
    const valueStr = sanitizePercent(raw);
    setEdits(prev => ({ ...prev, [id]: valueStr }));
  };

  const handleDeleteCommissionConfirm = async () => {
    if (deletedId === null) return;

    try {
      await dispatch(deleteCommission(brokerID, deletedId));
    } finally {
      setDeletedId(null);
      fileTableRef.current?.update?.();
    }
  };

  const handleCarrierConfirm = async values => {
    try {
      await dispatch(
        addCommission(brokerID, {
          ...values,
          commissionID: 0,
          overridePercentage: Number(values.overridePercentage) || 0,
          streetLevelPercentage: Number(values.streetLevelPercentage) || 0,
        }),
      );
      setCarrierOpen(false);
      fileTableRef.current?.update?.();
    } catch (err) {}
  };

  useEffect(() => {
    dispatch(fetchNewCommissionDTO(brokerID));
  }, []);

  const columns: ITableColumn<ICommission>[] = [
    {
      key: "carrierID",
      title: S.BRO_COMMISSIONS_CARRIER,
      style: { textAlign: "left" },
      render: item => <>{selectCarrierTypeList.find(c => c.value === item.carrierID)?.key}</>,
    },
    {
      key: "streetLevelPercentage",
      title: S.BRO_COMMISSIONS_STREET_LVL,
      style: { textAlign: "left" },
      render: item => `${item.streetLevelPercentage}%`,
    },
    {
      key: "overridePercentage",
      title: S.BRO_COMMISSIONS_OVERRIDE,
      style: { textAlign: "left" },
      render: item =>
        isEditing ? (
          <input
            className="broker-commissions__percentage-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={edits[item.commissionID] ?? ""}
            onChange={e => handleOverrideChange(item.commissionID, e.target.value)}
          />
        ) : (
          `${Number(item.overridePercentage).toString()}%`
        ),
    },
    {
      key: "startDate",
      title: S.BRO_COMMISSIONS_START_DATE,
      style: { textAlign: "left" },
      render: item => formatDate(item.startDate),
    },
    {
      key: "modifiedBy",
      title: S.BRO_COMMISSIONS_MODIFIED_BY,
      style: { textAlign: "left" },
    },
    {
      key: "actions",
      title: S.PRO_COL_ACTIONS,
      render: (item: ICommission) => {
        return (
          <TableActions
            key={item.commissionID}
            actions={[
              {
                label: S.BRO_COMMISSIONS_VIEW_LOGS_ACTION,
                handler: () => {
                  setSelectedCommissionId(item.commissionID);
                  setHistoryOpen(true);
                },
              },
              {
                label: S.BRO_COMMISSIONS_VIEW_COMMISSION_TABLE_ACTION,
                handler: () => {
                  setSelectedCommissionId(item.commissionID);
                  setRenewalOpen(true);
                  setSelectedCarrierId(item.carrierID);
                },
              },
              {
                label: S.BRO_COMMISSIONS_VIEW_DELETE_ACTION,
                handler: () => setDeletedId(item.commissionID),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <main className="broker-tab__wrap">
        <section className="broker-tab__container">
          <Container>
            <div className="broker-tab__form-wrap">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <h1 className="form-title">{S.BRO_COMMISSIONS_TITLE}</h1>

                <div style={{ display: "flex", gap: "16px" }}>
                  {!isEditing ? (
                    <>
                      <button className="button-v2 button-v2__secondary" onClick={() => setCarrierOpen(true)}>
                        {S.BRO_COMMISSIONS_ADD_CARRIER_LABEL}
                      </button>
                      <button className="button-v2 button-v2__primary broker-tab__action-button" onClick={startEditing}>
                        {S.BRO_COMMISSIONS_UPDATE_OVERRIDE_LABEL}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="button-v2 button-v2__secondary" onClick={cancelEditing}>
                        {S.BRO_COMMISSIONS_VIEW_CANCEL_LABEL}
                      </button>
                      <button className="button-v2 button-v2__primary" onClick={saveEditing}>
                        {S.BRO_COMMISSIONS_VIEW_SAVE_LABEL}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <Row>
                <Col className="doc-tab__table-override">
                  <DisTable<ICommission>
                    columns={columns}
                    initialState={{}}
                    getRef={ft => {
                      fileTableRef.current = ft;
                    }}
                    isPaginated={false}
                    onUpdate={async () => {
                      const result = await dispatch(fetchBrokerCommissionsList(brokerID));
                      const data = get(result, "value.data");
                      setCommissions(data);

                      return {
                        total: get(result, "value.data.length") as number,
                        data: data as ICommission[],
                      };
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>

      <CommissionHistoryModal
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setSelectedCommissionId(null);
        }}
        brokerId={brokerID}
        commissionId={selectedCommissionId}
      />

      <CarrierModal
        open={carrierOpen}
        onClose={() => setCarrierOpen(false)}
        onConfirm={handleCarrierConfirm}
        selectCarrierList={selectCarrierTypeList}
      />
      <CommissionTableModal
        open={renewalOpen}
        onClose={() => {
          setRenewalOpen(false);
          setSelectedCommissionId(null);
          setSelectedCarrierId(null);
        }}
        brokerId={brokerID}
        commissionId={selectedCommissionId}
        carrierId={selectedCarrierId}
      />

      {deletedId !== null && (
        <PromptModal
          open={deletedId !== null}
          title={S.BRO_COMMISSIONS_CONFIRM_TITLE}
          confirmText={S.BRO_COMMISSIONS_DELETE_CONFIRM_CONFIRM}
          rejectText={S.BRO_COMMISSIONS_DELETE_CONFIRM_REJECT}
          onConfirm={handleDeleteCommissionConfirm}
          onReject={() => setDeletedId(null)}>
          <Typography variant="h6">{S.BRO_COMMISSIONS_DELETE_CONFIRM_MESSAGE}</Typography>
        </PromptModal>
      )}
      <Toast open={loading} message={S.LOADING} onClose={() => {}} />
    </>
  );
};

export default BrokerCommissions;
